import { create } from 'zustand';

// Modal types enum for type safety
export const MODAL_TYPES = {
  DEPARTMENT: 'department',
  ADD_PERSON: 'addPerson',
  CONFIRMATION: 'confirmation',
  USER_PROFILE: 'userProfile',
  DOCUMENT_PREVIEW: 'documentPreview',
  UPLOAD_DOCUMENT: 'uploadDocument',
  EDIT_USER: 'editUser',
  CREATE_USER: 'createUser',
  SETTINGS: 'settings',
  FILTER: 'filter',
  CREATE_ORGANIZATION: 'createOrganization',
  ORGANIZATION_DETAILS: 'organizationDetails',
  FACILITY: 'facility',
  CATEGORY: 'category',
  SUBCATEGORY: 'subcategory',
  RESOURCE: 'resource',
  // Add more modal types as needed
};

export const useModalStore = create((set, get) => ({
  // State
  modals: [], // Array to support modal stacking

  // Actions
  openModal: (type, props = {}) => {
    const { modals } = get();
    const newModal = {
      id: `${type}-${Date.now()}`, // Unique ID for each modal instance
      type,
      props,
      isOpen: true,
    };

    set({
      modals: [...modals, newModal],
    });

    return newModal.id;
  },

  closeModal: (modalId) => {
    const { modals } = get();
    if (modalId) {
      // Close specific modal by ID
      set({
        modals: modals.filter((modal) => modal.id !== modalId),
      });
    } else {
      // Close the topmost modal
      set({
        modals: modals.slice(0, -1),
      });
    }
  },

  closeAllModals: () => {
    set({ modals: [] });
  },

  updateModalProps: (modalId, newProps) => {
    const { modals } = get();
    set({
      modals: modals.map((modal) =>
        modal.id === modalId
          ? { ...modal, props: { ...modal.props, ...newProps } }
          : modal,
      ),
    });
  },

  // Getters
  getTopModal: () => {
    const { modals } = get();
    return modals.length > 0 ? modals[modals.length - 1] : null;
  },

  isModalOpen: (type) => {
    const { modals } = get();
    return modals.some((modal) => modal.type === type);
  },

  getModalById: (modalId) => {
    const { modals } = get();
    return modals.find((modal) => modal.id === modalId);
  },

  getModalsByType: (type) => {
    const { modals } = get();
    return modals.filter((modal) => modal.type === type);
  },

  // Utility methods
  replaceModal: (type, props = {}) => {
    const { closeAllModals, openModal } = get();
    closeAllModals();
    return openModal(type, props);
  },

  // Department-specific helpers
  openDepartmentModal: (editingDepartment = null) => {
    const { openModal } = get();
    return openModal(MODAL_TYPES.DEPARTMENT, { editingDepartment });
  },

  // Confirmation modal helper
  openConfirmationModal: (config) => {
    const { openModal } = get();
    const defaultConfig = {
      title: 'Confirm Action',
      message: 'Are you sure you want to proceed?',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: () => {},
      onCancel: () => {},
      variant: 'default', // 'default', 'danger', 'warning'
    };
    return openModal(MODAL_TYPES.CONFIRMATION, { ...defaultConfig, ...config });
  },

  // Add Person modal helper
  openAddPersonModal: () => {
    const { openModal } = get();
    return openModal(MODAL_TYPES.ADD_PERSON);
  },

  // Filter modal helper
  openFilterModal: (currentFilters = {}, onApply = () => {}) => {
    const { openModal } = get();
    return openModal(MODAL_TYPES.FILTER, { currentFilters, onApply });
  },

  // Organization modal helpers
  openCreateOrganizationModal: (onSuccess = () => {}) => {
    const { openModal } = get();
    return openModal(MODAL_TYPES.CREATE_ORGANIZATION, { onSuccess });
  },

  openOrganizationDetailsModal: (organization, onUpdate = () => {}) => {
    const { openModal } = get();
    return openModal(MODAL_TYPES.ORGANIZATION_DETAILS, {
      organization,
      onUpdate,
    });
  },
}));

// Custom hooks for easier usage
export const useModal = () => {
  const {
    openModal,
    closeModal,
    closeAllModals,
    updateModalProps,
    getTopModal,
    isModalOpen,
    getModalById,
    replaceModal,
    openDepartmentModal,
    openConfirmationModal,
    openAddPersonModal,
    openFilterModal,
    openCreateOrganizationModal,
    openOrganizationDetailsModal,
  } = useModalStore();

  return {
    openModal,
    closeModal,
    closeAllModals,
    updateModalProps,
    getTopModal,
    isModalOpen,
    getModalById,
    replaceModal,
    // Specific modal helpers
    openDepartmentModal,
    openConfirmationModal,
    openAddPersonModal,
    openFilterModal,
    openCreateOrganizationModal,
    openOrganizationDetailsModal,
  };
};

// Hook to get all modals (for the ModalRenderer component)
export const useModals = () => {
  return useModalStore((state) => state.modals);
};
